import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Filter, Check, RotateCcw } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** Sort/filter options for the "All" dropdown pill — mirrors the SeeAll modal's filter. */
export type CategoryFilterKey = 'all' | 'new' | 'popular' | 'price_low' | 'price_high';
export const FILTER_LABELS: Record<CategoryFilterKey, string> = {
  all: 'All',
  new: 'Newest',
  popular: 'Popular',
  price_low: 'Price: Low to High',
  price_high: 'Price: High to Low',
};

interface CategoryChipsRowProps {
  /** Category list to render as pills — expected to include 'All' as the first entry. */
  categories: string[];
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  /**
   * Optional content rendered as the very first item in the row, before any
   * category chips (e.g. the Explore search control). Rendered even when
   * there are no category chips to show, so sections without categories
   * (Ringtones with no distinct categories, By Users) can still render a
   * row containing just this slot.
   */
  leadingSlot?: React.ReactNode;
  /**
   * When false, category chips (including the filter dropdown) are hidden but
   * `leadingSlot` (if provided) still renders. Defaults to true.
   */
  showCategories?: boolean;
  /** Currently selected sort/filter for the dropdown that replaces the "All" pill. */
  filterKey: CategoryFilterKey;
  onFilterChange: (key: CategoryFilterKey) => void;
  /**
   * When true, the two price sort options are omitted from the dropdown.
   * Used by By Users, where entries are creators rather than priced items.
   */
  hidePriceSort?: boolean;
}

/**
 * Horizontally-scrollable category filter chips — the same mint-accent pill
 * treatment already used by the "All Wallpapers" / "All Live" / "All Ringtones"
 * listing views. Used inline (not sticky) at the top of each Explore section
 * so Explore shows the exact same category filtering as those listing pages.
 *
 * The leading "All" pill is a dropdown (Radix DropdownMenu) offering "All" /
 * "Newest" / "Popular" / "Price: Low to High" / "Price: High to Low" sort
 * options — structurally identical to the See All modal's filter dropdown
 * (see SeeAllModal in HomePage.tsx). The pill's label always reads "All"
 * regardless of which sort is selected.
 */
export const CategoryChipsRow: React.FC<CategoryChipsRowProps> = ({
  categories,
  activeCategory,
  onCategoryChange,
  leadingSlot,
  showCategories = true,
  filterKey,
  onFilterChange,
  hidePriceSort = false,
}) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const otherCategories = categories.filter(cat => cat !== 'All');

  // Nothing to show at all — don't render an empty row.
  if (!showCategories && !leadingSlot) return null;

  // The dropdown trigger reads as "active" whenever this section is showing its
  // unfiltered view (activeCategory === 'All', or '' for sections with no distinct
  // categories like By Users) OR a sort option is applied.
  const isUnfiltered = activeCategory === 'All' || activeCategory === '';
  const triggerActive = filterKey !== 'all' || isUnfiltered;

  return (
    <div
      data-no-swipe
      className="hide-scrollbar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        overflowX: 'auto',
        padding: '9px 14px 12px',
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {leadingSlot}

      {showCategories && (
        <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
          <DropdownMenuTrigger asChild>
            <motion.button
              whileTap={{ scale: 0.94 }}
              data-active-category={triggerActive ? 'true' : undefined}
              style={{
                flexShrink: 0,
                padding: '5px 14px',
                borderRadius: 999,
                border: triggerActive ? 'none' : '1px solid rgba(255,255,255,0.13)',
                background: triggerActive ? '#46f5e0' : 'transparent',
                color: triggerActive ? '#0a0a10' : 'rgba(240,240,245,0.5)',
                fontSize: 12,
                fontWeight: triggerActive ? 700 : 500,
                fontFamily: "'Syne', sans-serif",
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                letterSpacing: triggerActive ? '-0.01em' : '0',
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                transition: 'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
              }}
            >
              <Filter size={11} strokeWidth={2.2} />
              All
            </motion.button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            sideOffset={8}
            style={{
              background: 'rgba(20,18,28,0.98)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 14,
              minWidth: 220,
              padding: 6,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
              zIndex: 10000,
            }}
          >
            <div style={{
              padding: '8px 12px 6px',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(240,240,245,0.42)',
              fontFamily: "'Syne', sans-serif",
            }}>
              Sort by
            </div>
            {(Object.entries(FILTER_LABELS) as [CategoryFilterKey, string][])
              .filter(([key]) => !(hidePriceSort && (key === 'price_low' || key === 'price_high')))
              .map(([key, label]) => {
              // For the "All" item, only mark selected when filterKey is 'all' AND the
              // section is showing its unfiltered view. For 'new', the checkmark follows
              // filterKey alone since selecting it always resets activeCategory to 'All'.
              const selected = key === 'all' ? (filterKey === 'all' && isUnfiltered) : filterKey === key;
              return (
                <DropdownMenuItem
                  key={key}
                  onSelect={(e) => { e.preventDefault(); onFilterChange(key); onCategoryChange('All'); setFilterOpen(false); }}
                  style={{
                    fontFamily: "'Syne', sans-serif",
                    fontSize: 13,
                    fontWeight: selected ? 700 : 500,
                    color: selected ? '#46f5e0' : 'rgba(240,240,245,0.78)',
                    cursor: 'pointer',
                    borderRadius: 8,
                    padding: '9px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <span>{label}</span>
                  {selected && <Check size={14} strokeWidth={2.5} color="#46f5e0" />}
                </DropdownMenuItem>
              );
            })}

            <div style={{
              height: 1,
              background: 'rgba(255,255,255,0.08)',
              margin: '6px 4px',
            }} />

            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); onFilterChange('all'); setFilterOpen(false); }}
              style={{
                fontFamily: "'Syne', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: filterKey !== 'all' ? '#f0f0f5' : 'rgba(240,240,245,0.4)',
                cursor: filterKey !== 'all' ? 'pointer' : 'default',
                borderRadius: 8,
                padding: '9px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <RotateCcw size={13} strokeWidth={2.2} />
              Reset filter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Remaining category pills — "All" is represented by the dropdown above. */}
      {showCategories && otherCategories.map((cat) => {
        const isActive = cat === activeCategory;
        return (
          <motion.button
            key={cat}
            whileTap={{ scale: 0.94 }}
            data-active-category={isActive ? 'true' : undefined}
            onClick={() => { onCategoryChange(cat); onFilterChange('all'); }}
            style={{
              flexShrink: 0,
              padding: '5px 14px',
              borderRadius: 999,
              border: isActive ? 'none' : '1px solid rgba(255,255,255,0.13)',
              background: isActive ? '#46f5e0' : 'transparent',
              color: isActive ? '#0a0a10' : 'rgba(240,240,245,0.5)',
              fontSize: 12,
              fontWeight: isActive ? 700 : 500,
              fontFamily: "'Syne', sans-serif",
              cursor: 'pointer',
              letterSpacing: isActive ? '-0.01em' : '0',
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              transition: 'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
            }}
          >
            {cat}
          </motion.button>
        );
      })}
    </div>
  );
};

export default CategoryChipsRow;
